/* ============================================================================
   Lockett Labs filter UI

   Drives LLFilter. This file owns presentation and interaction only; every
   question about which products match is asked of the engine.

   IT MOVES INTO THE EXISTING SHEET
     The collection section already ships a filter panel: a sidebar on desktop
     and a bottom sheet on phones, with a backdrop, a close button, an open
     trigger and a scroll lock that are all already wired and already tested.
     Rebuilding that would mean rebuilding those behaviours and their bugs. So
     the facet groups are injected into [data-filters] and everything around
     them keeps working untouched.

   IT REPLACES XCLOUD IN PLACE
     XCloud's mount and its availability block are hidden rather than removed,
     because the section still renders them and this has to be reversible by
     turning one setting off.

   STATE LIVES IN THE URL
     Selections are written to the query string with history.replaceState, so a
     filtered view can be shared, reloaded and reached with the back button.
     Reading it back on load is what makes those three work.
   ========================================================================== */

(function () {
  'use strict';

  var GROUPS = [
    /* Which range, for the collections that mix them: all, single-cards,
       under-25, sealed-product and the cross-game shelves.

       Labelled Category rather than Game because the list is not only games:
       the shard holding products with no game is game-agnostic accessories,
       and it sits in here beside Pokemon and Lorcana. "Product type" was
       already taken by the single / sealed / accessory group.

       minValues 2 keeps it off a single-game page, where every product would
       answer the same and the group would be a list of one. */
    { key: 'game', label: 'Category', minValues: 2 },
    /* Who made it. Accessories are shopped by brand far more than by game, so
       it sits straight under Category. The data is the product vendor, fetched
       per collection by loadVendors(); a collection with one vendor, or one
       too big to fetch, has no brand dictionary and the group never appears. */
    { key: 'brand', label: 'Brand', minValues: 2 },
    { key: 'product_class', label: 'Product type' },
    { key: 'series',        label: 'Series' },
    { key: 'set_name',      label: 'Set',      searchable: true },
    { key: 'language',      label: 'Language' }
  ];

  /* Dictionary values are handles, which are the wrong thing to show a
     shopper. Anything not listed falls back to a tidied handle. */
  var LABELS = {
    single: 'Single cards', sealed: 'Sealed product', accessory: 'Accessories',
    'plush-figure': 'Plush and figures',
    en: 'English', ja: 'Japanese', ko: 'Korean', zh: 'Chinese',
    'scarlet-violet': 'Scarlet & Violet', 'sword-shield': 'Sword & Shield',
    'sun-moon': 'Sun & Moon', 'black-white': 'Black & White',
    'mega-evolution': 'Mega Evolution', xy: 'XY',
    /* Shard names. These need spelling out properly rather than being
       title-cased into "Mtg" and "Yu Gi Oh".

       'other' is the shard for products carrying no game, and it is game
       agnostic accessories: 85 of its 86 products are product_class accessory
       and the 86th is a dice set. So it is named for what a shopper would
       recognise rather than for what the data calls it. No other facet uses
       'other' as a value, so this override is safe to make globally. */
    pokemon: 'Pokémon', 'one-piece': 'One Piece', lorcana: 'Lorcana',
    mtg: 'Magic: The Gathering', digimon: 'Digimon', 'yu-gi-oh': 'Yu-Gi-Oh!',
    'disney-merch': 'Disney', other: 'Accessories'
  };

  /* Shopify handleizes an apostrophe to a hyphen, so "The World's Strongest
     Warriors" reaches the index as the-world-s-strongest-warriors and listed
     out as "The World S Strongest Warriors". A lone s between hyphens is a
     possessive here, never a word.

     Capitalising on \b compounds it: an apostrophe is a word boundary, so \b\w
     also hits the s in "World's". Case is applied per space-delimited word. */
  /* Brand keys are handles of the vendor ("pokemon-accessories"), and title
     casing a handle loses the accent and the vendor's own capitals
     ("Pokemon Accessories", "Gogogames"). The real names come back with the
     vendor feed and are used as they are. */
  var BRAND_NAMES = {};

  function pretty(value) {
    if (LABELS[value]) return LABELS[value];
    if (BRAND_NAMES[value]) return BRAND_NAMES[value];
    return String(value)
      .replace(/-s-/g, "'s-")
      .replace(/-s$/, "'s")
      .replace(/-/g, ' ')
      .replace(/(^|\s)\S/g, function (m) { return m.toUpperCase(); });
  }

  var state = { selection: {}, sort: 'featured', page: 0, handles: [], total: 0 };
  var root, grid, sheet, panel, summary, moreBtn, nativeSort;

  /* ---- URL ------------------------------------------------------------- */

  function readUrl() {
    var params = new URLSearchParams(location.search);
    var sel = {};
    GROUPS.forEach(function (g) {
      var raw = params.get('f.' + g.key);
      if (raw) sel[g.key] = raw.split(',').filter(Boolean);
    });
    if (params.get('f.preorder') === '1') sel.preorder = true;
    if (params.get('f.onsale') === '1') sel.onSale = true;
    if (params.get('f.newin') === '1') sel.newIn = true;
    var price = params.get('f.price');
    if (price && price.indexOf('-') > -1) {
      var parts = price.split('-');
      if (Number(parts[0]) > 0) sel.priceMin = Number(parts[0]);
      if (Number(parts[1]) > 0) sel.priceMax = Number(parts[1]);
    }
    var avail = params.get('f.availability');
    if (avail) sel.availability = avail.split(',').filter(Boolean);
    return sel;
  }

  function writeUrl() {
    var params = new URLSearchParams(location.search);
    GROUPS.forEach(function (g) { params.delete('f.' + g.key); });
    params.delete('f.preorder');
    GROUPS.forEach(function (g) {
      var chosen = state.selection[g.key];
      if (chosen && chosen.length) params.set('f.' + g.key, chosen.join(','));
    });
    if (state.selection.preorder) params.set('f.preorder', '1');
    params.delete('f.onsale');
    params.delete('f.newin');
    if (state.selection.onSale) params.set('f.onsale', '1');
    if (state.selection.newIn) params.set('f.newin', '1');
    params.delete('f.availability');
    if ((state.selection.availability || []).length) {
      params.set('f.availability', state.selection.availability.join(','));
    }
    /* The theme's server pager is still in the markup and still links to
       ?page=N. Once this engine owns the grid that parameter is a lie: every
       page reloaded and re-rendered the SAME first 24 products, because the
       engine always starts at its own page 0. The pager is hidden and the
       parameter is dropped so a stale link cannot resurrect it. */
    params.delete('f.price');
    if (state.selection.priceMin != null || state.selection.priceMax != null) {
      params.set('f.price', (state.selection.priceMin || 0) + '-' + (state.selection.priceMax || 0));
    }
    params.delete('page');
    params.delete('sort');
    if (state.sort && state.sort !== 'featured') params.set('sort', state.sort);

    /* Shopify's own sort parameter is kept in step rather than left stale. The
       section decides which option to mark selected from search.sort_by, so a
       leftover value would render a control that disagrees with the grid on the
       next load; and with JavaScript off the server still sorts by it. Search
       honours only the two price sorts, so the rest are ignored there, which is
       harmless. */
    params.delete('sort_by');
    if (state.sort && state.sort !== 'featured') {
      var chosen = LLFilter.sorts.filter(function (s) { return s.key === state.sort; })[0];
      if (chosen && chosen.native) params.set('sort_by', chosen.native);
    }
    var qs = params.toString();
    history.replaceState(null, '', location.pathname + (qs ? '?' + qs : ''));
  }

  /* ---- markup ----------------------------------------------------------- */

  function buildPanel() {
    panel = document.createElement('div');
    panel.className = 'llf';

    summary = document.createElement('div');
    summary.className = 'llf__summary';
    panel.appendChild(summary);


    /* Availability first among the facets. On a singles catalogue it is the
       one most people reach for, and it is the only facet whose default
       behaviour is doing something on their behalf already. */
    var avail = document.createElement('div');
    avail.className = 'llf__group llf__group--avail';
    avail.innerHTML = '<div class="llf__group-head llf__group-head--static">Availability</div>';
    var availOpts = document.createElement('div');
    availOpts.className = 'llf__opts';
    availOpts.setAttribute('data-llf-opts', 'availability');
    /* Coming Soon sits last, below Out of stock, by the owner's call: the two
       stock states are the pair a shopper compares, and pre-orders are a
       separate question rather than a midpoint between them. */
    [['in', 'In stock'], ['out', 'Out of stock'], ['coming', 'Coming soon']].forEach(function (pair) {
      var label = document.createElement('label');
      label.className = 'llf__opt';
      label.setAttribute('data-value', pair[0]);
      label.innerHTML = '<input type="checkbox" data-llf-avail="' + pair[0] + '">' +
        '<span>' + pair[1] + '</span><span class="llf__n" data-llf-avail-n="' + pair[0] + '">0</span>';
      availOpts.appendChild(label);
    });
    avail.appendChild(availOpts);
    panel.appendChild(avail);

    panel.appendChild(buildPriceGroup());

    GROUPS.forEach(function (g) {
      var values = (LLFilter.dict[g.key] || []);
      if (values.length < (g.minValues || 1)) return;

      var group = document.createElement('details');
      group.className = 'llf__group';
      group.open = g.key === 'product_class' || g.key === 'game' || g.key === 'brand';
      group.setAttribute('data-group', g.key);

      var sum = document.createElement('summary');
      sum.className = 'llf__group-head';
      sum.innerHTML = '<span>' + g.label + '</span><span class="llf__chev" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" ' +
        'stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg></span>';
      group.appendChild(sum);

      if (g.searchable) {
        var search = document.createElement('input');
        search.type = 'search';
        search.className = 'llf__search';
        search.placeholder = 'Search ' + g.label.toLowerCase();
        search.setAttribute('data-llf-search', g.key);
        group.appendChild(search);
      }

      var list = document.createElement('div');
      list.className = 'llf__opts';
      list.setAttribute('data-llf-opts', g.key);
      group.appendChild(list);

      panel.appendChild(group);
    });

    /* Three quick states rather than a facet each: they are yes or no, they
       are what a card shopper actually asks for, and they cost one bit or one
       date comparison. Rarity and condition would belong here too, but this
       store's rarity and condition metafields are all but empty, and an empty
       facet is worse than a missing one. */
    [
      ['preorder', 'Pre-order only'],
      ['onsale',   'On offer'],
      ['newin',    'New in, last 30 days']
    ].forEach(function (pair) {
      var flag = document.createElement('label');
      flag.className = 'llf__opt llf__opt--flag';
      flag.innerHTML = '<input type="checkbox" data-llf-flag="' + pair[0] + '">' +
        '<span>' + pair[1] + '</span>' +
        '<span class="llf__n" data-llf-flag-n="' + pair[0] + '">0</span>';
      panel.appendChild(flag);
    });

    var clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'llf__clear';
    clear.setAttribute('data-llf-clear', '');
    clear.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" ' +
      'stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
      '<span data-llf-clear-label>Clear filters</span>';
    clear.hidden = true;
    panel.appendChild(clear);

    return panel;
  }

  /* Price is not a list of values, so it does not go through the dictionary
     path the other facets use. It gets a range with two number inputs and a
     two handle slider, plus the quick brackets.

     The brackets are mutually exclusive on purpose. Under 25, under 75 and
     under 100 are nested, so as checkboxes ticking two says nothing the wider
     one did not already say, and ticking all three reads as a filter while
     doing nothing. They behave as one choice that sets the ceiling, and
     picking the active one again clears it. */
  var BRACKETS = [[0, 'Any'], [2500, '\u00A325'], [7500, '\u00A375'], [10000, '\u00A3100']];

  function money(pence) {
    return '£' + (pence / 100).toFixed(2);
  }

  function buildPriceGroup() {
    var group = document.createElement('details');
    group.className = 'llf__group llf__group--price';
    group.open = true;
    group.setAttribute('data-group', 'price');

    group.innerHTML =
      '<summary class="llf__group-head"><span>Price</span>' +
        '<span class="llf__chev" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" ' +
        'stroke="currentColor" stroke-width="2.2" stroke-linecap="round" ' +
        'stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg></span></summary>' +
      '<div class="llf__price">' +
        '<div class="llf__price-inputs">' +
          '<label class="llf__price-field"><span>£</span>' +
            '<input type="number" inputmode="decimal" min="0" step="0.01" data-llf-price-min></label>' +
          '<span class="llf__price-dash">to</span>' +
          '<label class="llf__price-field"><span>£</span>' +
            '<input type="number" inputmode="decimal" min="0" step="0.01" data-llf-price-max></label>' +
        '</div>' +
        '<div class="llf__slider">' +
          '<div class="llf__slider-track"></div>' +
          '<div class="llf__slider-fill" data-llf-fill></div>' +
          '<input type="range" class="llf__range" data-llf-range="min" aria-label="Minimum price">' +
          '<input type="range" class="llf__range" data-llf-range="max" aria-label="Maximum price">' +
        '</div>' +
        '<div class="llf__price-ends"><span data-llf-price-lo></span><span data-llf-price-hi></span></div>' +
        '<div class="llf__brackets" data-llf-brackets></div>' +
      '</div>';

    var host = group.querySelector('[data-llf-brackets]');
    host.setAttribute('role', 'group');
    host.setAttribute('aria-label', 'Maximum price');
    BRACKETS.forEach(function (b) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'llf__bracket';
      btn.setAttribute('data-llf-bracket', String(b[0]));
      /* Price on its own line, count beneath and quiet. On this catalogue the
         three counts land within 300 of each other out of 13,169, so at equal
         weight they read as three identical numbers and drown the prices they
         are meant to qualify. */
      btn.innerHTML =
        '<span class="llf__bracket-label">' + b[1] + '</span>' +
        (b[0] ? '<span class="llf__bracket-n" data-llf-bracket-n="' + b[0] + '">0</span>' : '');
      host.appendChild(btn);
    });

    return group;
  }

  /* Bounds come from the current selection with price removed, so the slider
     always spans what is actually reachable rather than what was reachable
     when the page loaded. */
  function renderPrice(result) {
    /* counts() hangs these off the COUNTS object, not the result, so
       result.priceRange is always undefined and the fallback made the whole
       control render as an empty 0 to 100 range with zero counts. */
    var range = (result.counts && result.counts.priceRange) || { min: 0, max: 0 };
    var lo = range.min;
    var hi = range.max;
    if (hi <= lo) hi = lo + 100;

    var minInput = panel.querySelector('[data-llf-price-min]');
    var maxInput = panel.querySelector('[data-llf-price-max]');
    var rangeMin = panel.querySelector('[data-llf-range="min"]');
    var rangeMax = panel.querySelector('[data-llf-range="max"]');

    [rangeMin, rangeMax].forEach(function (el) {
      el.min = String(lo);
      el.max = String(hi);
      el.step = '1';
    });

    var selMin = state.selection.priceMin != null ? state.selection.priceMin : lo;
    var selMax = state.selection.priceMax != null ? state.selection.priceMax : hi;
    rangeMin.value = String(selMin);
    rangeMax.value = String(selMax);

    /* Placeholders show the full range, values only once the shopper has
       chosen, so an untouched control does not look like an applied filter. */
    minInput.placeholder = (lo / 100).toFixed(2);
    maxInput.placeholder = (hi / 100).toFixed(2);
    minInput.value = state.selection.priceMin != null ? (selMin / 100).toFixed(2) : '';
    maxInput.value = state.selection.priceMax != null ? (selMax / 100).toFixed(2) : '';

    panel.querySelector('[data-llf-price-lo]').textContent = money(selMin);
    panel.querySelector('[data-llf-price-hi]').textContent = money(selMax);

    var fill = panel.querySelector('[data-llf-fill]');
    var span = hi - lo || 1;
    fill.style.left = ((selMin - lo) / span * 100) + '%';
    fill.style.right = (100 - (selMax - lo) / span * 100) + '%';

    var counts = (result.counts && result.counts.priceBrackets) || {};
    BRACKETS.forEach(function (b) {
      var el = panel.querySelector('[data-llf-bracket-n="' + b[0] + '"]');
      if (el) el.textContent = counts[b[0]] || 0;
      /* Under 25 on a collection where nothing is under 25 is a dead button.
         Any has no count and always stays, so there is always a way back. */
      var bracketBtn = panel.querySelector('[data-llf-bracket="' + b[0] + '"]');
      if (bracketBtn && b[0]) {
        bracketBtn.hidden = !(counts[b[0]] > 0) && state.selection.priceMax !== b[0];
      }
      var btn = panel.querySelector('[data-llf-bracket="' + b[0] + '"]');
      if (btn) {
        var noRange = state.selection.priceMin == null && state.selection.priceMax == null;
        var active = b[0] === 0
          ? noRange
          : (state.selection.priceMax === b[0] && state.selection.priceMin == null);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      }
    });
  }

  function renderOptions(result) {
    GROUPS.forEach(function (g) {
      var host = panel.querySelector('[data-llf-opts="' + g.key + '"]');
      if (!host) return;
      var values = LLFilter.dict[g.key] || [];
      var tally = result.counts[g.key] || {};
      var chosen = state.selection[g.key] || [];

      /* Ticked first, then by how many products a value has.

         Count order alone put the useful options at the top of a 159 entry set
         list rather than leaving them in dictionary order, which was right as
         far as it went. What it did not do was keep the options a shopper had
         ALREADY chosen where they could see them: tick Evolving Skies and it
         stayed wherever its product count happened to put it, three rows down
         a scrolling list, so the one value they were filtering on was the one
         they had to hunt for to undo. Selected values now rise to the top of
         their group, which also means the whole of the current selection is
         readable without scrolling.

         Within each block the count order is kept, so nothing else moves. */
      var order = values.map(function (v, i) { return { v: v, i: i, n: tally[i] || 0 }; })
        .filter(function (o) { return o.n > 0 || chosen.indexOf(o.v) > -1; })
        .sort(function (a, b) {
          var aOn = chosen.indexOf(a.v) > -1 ? 0 : 1;
          var bOn = chosen.indexOf(b.v) > -1 ? 0 : 1;
          if (aOn !== bOn) return aOn - bOn;
          return b.n - a.n;
        });

      /* A group with nothing in it is a heading that promises options and
         then wastes a row saying nothing. A group with exactly ONE option is
         no better: on a set collection every card is the same set, the same
         series and the same language, so Set, Series and Language each offer
         a single tickbox that cannot change what is on screen. Four headings,
         no choices.

         So a facet appears once it can actually divide the collection, which
         takes two options. A group holding a ticked option always stays, or
         the shopper could not undo it. */
      var groupEl = panel.querySelector('[data-group="' + g.key + '"]');
      if (groupEl) groupEl.hidden = order.length < 2 && chosen.length === 0;

      host.innerHTML = '';
      order.forEach(function (o) {
        var label = document.createElement('label');
        label.className = 'llf__opt';
        label.setAttribute('data-value', o.v);
        label.setAttribute('data-search', pretty(o.v).toLowerCase());
        var checked = chosen.indexOf(o.v) > -1 ? ' checked' : '';
        label.innerHTML = '<input type="checkbox" data-llf-value="' + g.key + '"' + checked + '>' +
          '<span>' + pretty(o.v) + '</span><span class="llf__n">' + o.n + '</span>';
        host.appendChild(label);
      });
    });

    renderPrice(result);

    var flagCounts = {
      preorder: result.counts.preorder || 0,
      onsale: result.counts.onSale || 0,
      newin: result.counts.newIn || 0
    };
    /* A count of zero means ticking it would empty the grid, so the row is
       just taking up space. It comes back the moment something qualifies.

       A row the shopper has already ticked always stays, even at zero, or
       they would have no way to untick it and no way to see why the grid
       went empty. */
    Object.keys(flagCounts).forEach(function (key) {
      var el = panel.querySelector('[data-llf-flag-n="' + key + '"]');
      if (el) el.textContent = flagCounts[key];
      var box = panel.querySelector('[data-llf-flag="' + key + '"]');
      if (!box) return;
      box.checked = !!state.selection[key === 'onsale' ? 'onSale' : key === 'newin' ? 'newIn' : 'preorder'];
      var row = box.closest('.llf__opt');
      if (row) row.hidden = flagCounts[key] === 0 && !box.checked;
    });

    var stock = result.counts.availability || { in: 0, coming: 0, out: 0 };
    var availShown = 0;
    /* Each row hides itself when its count is zero, so a collection with no
       pre-orders simply shows the two states it has. */
    ['in', 'out', 'coming'].forEach(function (k) {
      var el = panel.querySelector('[data-llf-avail-n="' + k + '"]');
      if (el) el.textContent = stock[k] || 0;
      var box = panel.querySelector('[data-llf-avail="' + k + '"]');
      if (!box) return;
      box.checked = (state.selection.availability || []).indexOf(k) > -1;
      var row = box.closest('.llf__opt');
      if (row) {
        row.hidden = !(stock[k] > 0) && !box.checked;
        if (!row.hidden) availShown++;
      }
    });
    /* Everything in stock, or nothing: either way the pair says nothing worth
       a heading, so the whole block goes rather than leaving a lone chip. */
    var availGroup = panel.querySelector('.llf__group--avail');
    if (availGroup) availGroup.hidden = availShown < 2;
  }

  /* ---- run -------------------------------------------------------------- */

  /* How many filters are actually applied. The control used to sit there at
     full weight with nothing to clear, which is a button that lies about
     having work to do, and it gave no idea how much it would undo. */
  function appliedCount() {
    var n = 0;
    GROUPS.forEach(function (g) {
      n += (state.selection[g.key] || []).length;
    });
    n += (state.selection.availability || []).length;
    if (state.selection.preorder) n++;
    if (state.selection.onSale) n++;
    if (state.selection.newIn) n++;
    if (state.selection.priceMin != null || state.selection.priceMax != null) n++;
    return n;
  }

  function refresh(resetPage) {
    if (resetPage !== false) state.page = 0;
    var result = LLFilter.query(state.selection, state.sort);
    state.handles = result.handles;
    state.total = result.handles.length;

    renderOptions(result);
    writeUrl();

    var applied = appliedCount();
    var any = applied > 0;

    summary.innerHTML = '<b>' + state.total.toLocaleString() + '</b> ' +
      (state.total === 1 ? 'product' : 'products') +
      (any ? ' <span class="llf__filtered">filtered</span>' : '');

    var clearBtn = panel.querySelector('[data-llf-clear]');
    if (clearBtn) {
      clearBtn.hidden = !any;
      var lbl = clearBtn.querySelector('[data-llf-clear-label]');
      if (lbl) lbl.textContent = 'Clear ' + applied + ' filter' + (applied === 1 ? '' : 's');
    }

    /* The drawer's "Show N results" button, which exists only in sheet mode.
       Same number as the summary line directly above, written in the same pass
       so the two cannot disagree: a button promising a count the panel has
       already contradicted is worse than no button. The section renders it with
       the collection total, and this is what makes it follow the selection.

       Queried off the document rather than `panel`, because `panel` is the .llf
       block this file builds and the button is rendered by the section, outside
       it. Keeping it in the section is deliberate: it is then present before
       this script runs, so it still closes the drawer if the engine stands
       down, and only its number goes stale. */
    var applyLabel = document.querySelector('[data-llf-apply-label]');
    if (applyLabel) {
      /* Whole label rather than just the number, so the plural and the zero
         case are written by whoever knows the count. "Show 0 products" is a
         button offering nothing; at zero it names the situation instead, and
         the panel's own "0 products filtered" line sits directly above it. */
      applyLabel.textContent = state.total === 0
        ? 'No matching products'
        : 'Show ' + state.total.toLocaleString() + ' product' + (state.total === 1 ? '' : 's');
    }

    grid.setAttribute('data-llf-busy', '1');
    return LLFilter.show(grid, state.handles, state.page).then(function () {
      grid.removeAttribute('data-llf-busy');
      updateMore();
      updateShown();
    });
  }

  /* The section renders "N on this page" from its own page size. Once this
     engine owns the grid that number is whatever the engine drew, so it is
     rewritten rather than left contradicting the cards underneath it. */
  function updateShown() {
    var el = document.querySelector('[data-shown-count]');
    if (!el || !grid) return;
    el.textContent = String(grid.querySelectorAll('.llc__card').length);
  }

  /* Progress rather than a bare label. "1,976 left" tells a shopper nothing
     about where they are in 13,169 products; "48 of 13,169" does, and the bar
     makes it legible without reading either number. */
  function updateMore() {
    if (!moreBtn) return;
    var shown = Math.min(grid.querySelectorAll('.llc__card').length, state.total);
    var done = shown >= state.total;

    /* No results at all is the empty state's job, and it says so properly.
       Without this the control announced "That is everything, 0 of 0" directly
       underneath it. */
    if (!state.total) {
      moreBtn.hidden = true;
      return;
    }

    moreBtn.hidden = false;
    moreBtn.disabled = done;
    moreBtn.setAttribute('data-llf-done', done ? '1' : '0');
    moreBtn.style.setProperty('--llf-progress',
      (state.total ? Math.round(shown / state.total * 100) : 100) + '%');

    if (done) {
      /* Finished, so this stops being a control and becomes a rule across the
         page with a quiet label on it. Different markup rather than different
         paint: a bordered box that cannot be pressed reads as a broken button,
         however it is styled.

         "1 of 1" was the giveaway. A progress count is the right thing to read
         while there is more to load and nonsense once there is not, so the
         finished state states the size of the result set instead. */
      moreBtn.innerHTML =
        '<span class="llf__end-rule" aria-hidden="true"></span>' +
        '<span class="llf__end-text">' +
          '<span class="llf__more-label">That is everything</span>' +
          '<span class="llf__more-sub">' +
            (state.total === 1 ? '1 product' :
              'All ' + state.total.toLocaleString() + ' products') +
          '</span>' +
        '</span>' +
        '<span class="llf__end-rule" aria-hidden="true"></span>';
      return;
    }

    moreBtn.innerHTML =
      '<span class="llf__more-label">Show more</span>' +
      '<span class="llf__more-sub">' +
        shown.toLocaleString() + ' of ' + state.total.toLocaleString() +
      '</span>';
  }

  function loadMore() {
    state.page += 1;
    var start = state.page * LLFilter.pageSize;
    var slice = state.handles.slice(start, start + LLFilter.pageSize);
    moreBtn.disabled = true;
    moreBtn.setAttribute('data-llf-loading', '1');
    /* Appends rather than replacing, so the shopper keeps their place. */
    LLFilter.append(grid, slice).then(function () {
      moreBtn.disabled = false;
      moreBtn.removeAttribute('data-llf-loading');
      updateMore();
      updateShown();
    });
  }

  /* The section already renders Shopify's sort control, a form with a
     select[name=sort_by] that reloads the page with ?sort_by=. That is
     correct when Shopify is ordering the grid and useless once this engine is,
     because the reload lands and the engine immediately repaints in its own
     order. Two controls that disagree is worse than one, so the existing one
     is adopted: its submit is suppressed, the options it cannot honour are
     removed, and changing it sorts through the engine. */
  function adoptNativeSort() {
    /* Collections render Shopify's own control, which carries name="sort_by".
       The search section renders its own, which does not: it is keyed by
       [data-lls-sort] instead. Matching only on the name meant search was the
       one page where adoption silently did not happen, so the select fell
       through to the section's own handler, reloaded with ?sort_by=, and the
       engine then repainted the grid in its own order. The reported symptom
       was "sorting does nothing and the control snaps back to Relevance". */
    var select = document.querySelector('select[name="sort_by"], select[data-lls-sort]');
    if (!select) return;

    var supported = {};
    LLFilter.sorts.forEach(function (s) { supported[s.native] = s.key; });

    /* Search offers Relevance, which no collection does and which is therefore
       absent from LLFilter.sorts. It is not a missing sort: on a search the
       members list IS Shopify's ranking and COL.order records each product's
       position in it, so `featured` already means relevance here. Mapping it
       keeps the option; without this the pruning below would delete it and
       leave a shopper no way back to the default order. */
    supported.relevance = 'featured';

    Array.prototype.slice.call(select.options).forEach(function (opt) {
      if (!supported[opt.value]) opt.remove();
    });

    /* Best selling is a real Shopify sort, but this theme's select does not
       offer it, so it has to be added. Several collections are SET to Best
       selling as their default, which meant the one order the merchant had
       chosen was the one a shopper could not pick.

       The value is Shopify's own, so with JavaScript off the form still
       submits ?sort_by=best-selling and Shopify sorts it server side. */
    if (!select.querySelector('option[value="best-selling"]')) {
      var bs = document.createElement('option');
      bs.value = 'best-selling';
      bs.textContent = 'Best selling';
      var manual = select.querySelector('option[value="manual"]');
      if (manual && manual.nextSibling) select.insertBefore(bs, manual.nextSibling);
      else select.appendChild(bs);
    }

    /* The control carries onchange="this.form.submit()" as an inline attribute.
       That matters: form.submit(), the METHOD, does not fire a submit event and
       cannot be cancelled, so a submit listener never sees it and the page
       reloads regardless. The attribute itself has to come off.

       Both forms are cleared because removeAttribute alone leaves an already
       compiled handler attached in some browsers. */
    select.removeAttribute('onchange');
    select.onchange = null;

    var form = select.closest('form');
    if (form) {
      /* Still worth having for a keyboard submit or a stray submit button. */
      form.addEventListener('submit', function (e) { e.preventDefault(); });
    }

    select.addEventListener('change', function () {
      state.sort = supported[select.value] || 'featured';
      refresh();
    }, true);

    /* Tells the search section's own handler to stand down. That handler
       reloads the page, which is right while Shopify is ordering the grid and
       destructive once this engine is. It cannot simply be deleted: when the
       index fails to load the engine stands down before ever reaching here,
       and the reload is then the only sorting the page has. */
    select.setAttribute('data-ll-adopted', '1');

    /* Start in whatever order the page was rendered in. The section marks the
       option matching search.sort_by as selected, so landing on a shared
       ?sort_by=price-descending link now opens sorted rather than sorting only
       after the shopper touches the control. boot() applies ?sort and any
       collection default after this, so both still win where they apply. */
    if (supported[select.value]) state.sort = supported[select.value];

    nativeSort = select;
    return select;
  }

  var priceTimer;

  function wire() {
    /* The slider fires on every pixel of a drag. Refresh is cheap but not free
       at 13,000 rows, so the visual updates immediately and the actual query
       waits until the drag settles. */
    panel.addEventListener('input', function (e) {
      var which = e.target.getAttribute && e.target.getAttribute('data-llf-range');
      if (!which) return;
      var rangeMin = panel.querySelector('[data-llf-range="min"]');
      var rangeMax = panel.querySelector('[data-llf-range="max"]');
      var a = Number(rangeMin.value);
      var b = Number(rangeMax.value);
      /* Handles must not cross, or min above max would select nothing. */
      if (a > b) { if (which === 'min') rangeMin.value = String(b); else rangeMax.value = String(a); }
      state.selection.priceMin = Number(rangeMin.value);
      state.selection.priceMax = Number(rangeMax.value);
      panel.querySelector('[data-llf-price-lo]').textContent = money(state.selection.priceMin);
      panel.querySelector('[data-llf-price-hi]').textContent = money(state.selection.priceMax);
      clearTimeout(priceTimer);
      priceTimer = setTimeout(function () { refresh(); }, 260);
    });

    panel.addEventListener('click', function (e) {
      var bracket = e.target.closest && e.target.closest('[data-llf-bracket]');
      if (!bracket) return;
      var ceiling = Number(bracket.getAttribute('data-llf-bracket'));
      var alreadyOn = state.selection.priceMax === ceiling && state.selection.priceMin == null;
      state.selection.priceMin = null;
      /* Zero is the Any segment, which clears rather than setting a ceiling. */
      state.selection.priceMax = (ceiling === 0 || alreadyOn) ? null : ceiling;
      refresh();
    });

    panel.addEventListener('change', function (e) {
      var box = e.target;
      var flag = box.getAttribute('data-llf-flag');
      if (flag) {
        var key = flag === 'onsale' ? 'onSale' : flag === 'newin' ? 'newIn' : 'preorder';
        state.selection[key] = box.checked;
        refresh();
        return;
      }
      if (box.hasAttribute('data-llf-price-min') || box.hasAttribute('data-llf-price-max')) {
        var raw = box.value.trim();
        var pence = raw === '' ? null : Math.round(parseFloat(raw) * 100);
        if (box.hasAttribute('data-llf-price-min')) state.selection.priceMin = pence;
        else state.selection.priceMax = pence;
        refresh();
        return;
      }

      var avail = box.getAttribute('data-llf-avail');
      if (avail) {
        var picked = state.selection.availability || (state.selection.availability = []);
        var seen = picked.indexOf(avail);
        if (box.checked && seen === -1) picked.push(avail);
        if (!box.checked && seen > -1) picked.splice(seen, 1);
        refresh();
        return;
      }
      var facet = box.getAttribute('data-llf-value');
      if (!facet) return;
      var value = box.closest('.llf__opt').getAttribute('data-value');
      var list = state.selection[facet] || (state.selection[facet] = []);
      var at = list.indexOf(value);
      if (box.checked && at === -1) list.push(value);
      if (!box.checked && at > -1) list.splice(at, 1);
      refresh();
    });

    panel.addEventListener('input', function (e) {
      var facet = e.target.getAttribute && e.target.getAttribute('data-llf-search');
      if (!facet) return;
      var q = (e.target.value || '').toLowerCase().trim();
      panel.querySelectorAll('[data-llf-opts="' + facet + '"] .llf__opt').forEach(function (o) {
        o.hidden = q !== '' && o.getAttribute('data-search').indexOf(q) === -1;
      });
    });

    panel.addEventListener('click', function (e) {
      if (!e.target.closest('[data-llf-clear]')) return;
      state.selection = {};
      state.sort = 'featured';
      panel.querySelectorAll('input[type=checkbox]').forEach(function (b) { b.checked = false; });
      if (nativeSort) nativeSort.value = 'manual';
      refresh();
    });
  }

  /* ---- boot ------------------------------------------------------------- */

  function collectionHandle() {
    var m = location.pathname.match(/\/collections\/([a-z0-9\-]+)/i);
    return m ? m[1].toLowerCase() : null;
  }

  /* asset_url appends a cache busting query, so a base URL cannot be built by
     trimming the filename off: the first attempt at that produced
     .../assets/?v=1206527...ll-index-pokemon.json. Only the filename is
     swapped, which keeps the ?v= where it belongs. Shards, map and member
     lists are regenerated together, so one version busting all of them is
     exactly the behaviour we want. */
  function swap(base, file) {
    return base.replace('ll-index-pokemon.json', file);
  }

  /* Swaps the filename but KEEPS the query, so a URL keeps the cache buster
     belonging to the asset it came from. Used to hang the membership lists off
     the map's own version rather than the Pokemon shard's. */
  function withFile(url, file) {
    var q = url.indexOf('?');
    var query = q > -1 ? url.slice(q) : '';
    var stem = q > -1 ? url.slice(0, q) : url;
    return stem.replace(/[^/]+$/, file) + query;
  }

  /* The section hands over a real asset_url per shard and for the map. Falling
     back to swapping the Pokemon base keeps an older section rendering. */
  var assetUrls = {};

  function shardUrl(base, game) {
    return assetUrls[game] || swap(base, 'll-index-' + game + '.json');
  }

  function mapUrl(base) {
    return assetUrls.map || swap(base, 'll-collection-map.json');
  }

  /* A membership list changes without its own filename changing, so it is
     versioned by the map it was recorded in, plus the signature of the list
     itself for the case where a collection swaps one product for another and
     its count does not move. */
  function membersUrl(base, handle, entry) {
    var url = withFile(mapUrl(base), 'll-members-' + handle + '.json');
    if (entry && entry.sig) url += (url.indexOf('?') > -1 ? '&' : '?') + 's=' + entry.sig;
    return url;
  }

  function fetchJson(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error(url.split('/').pop().split('?')[0] + ' ' + r.status);
      return r.json();
    });
  }

  /* The same map, asked for in a way that cannot come back stale.

     WHY THIS IS NEEDED
       The map URL carries a ?v= hash that Liquid stamps into the page HTML, so
       a shopper holding a cached copy of that HTML asks for the map as it stood
       when their copy was rendered. That request succeeds: the old file is
       still on the CDN under a year of max-age, correctly, because a versioned
       URL never changes. So a collection added to the map since then is simply
       absent, and the page stands down as if it were unscopable.

       Seen on 2026-09-08. Every fresh load was correct while a browser that had
       held the page since before the fix kept showing 'Filters could not load',
       and no amount of republishing could reach it, because nothing server side
       can reach into a cached page and change the URL written in it.

     WHAT DOES NOT WORK, since it is the obvious thing to try
       Asking for the asset without its ?v=, or with a cache buster of our own.
       Shopify's CDN keys theme assets on v and ignores query parameters it does
       not know, so //...ll-collection-map.json?llf=<now> is served from
       whatever it happens to be holding. Measured on 2026-09-08: that URL came
       back with an Age of 161,907 seconds and a map generated three days
       earlier. It would have replaced a stale map with an older one.

     WHAT DOES WORK
       The page HTML is not cached at the edge for any length of time, so
       fetching it again with cache: 'reload' produces a document carrying
       today's ?v=. The asset URLs are read back out of that, and the map is
       fetched by its current version like any other request. One page fetch,
       only on a miss, and only once. */
  function fetchMapPastCache() {
    return fetch(location.pathname + location.search,
                 { headers: { Accept: 'text/html' }, cache: 'reload' })
      .then(function (r) {
        if (!r.ok) throw new Error('page ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var m = html.match(/data-ll-assets="([^"]*)"/);
        if (!m) throw new Error('no asset list in page');
        var assets = JSON.parse(m[1].replace(/&quot;/g, '"')
                                    .replace(/&#39;/g, "'")
                                    .replace(/&amp;/g, '&'));
        if (!assets.map) throw new Error('no map url in page');
        return fetchJson(assets.map);
      });
  }

  /* Turns a collection handle into the shards to load and the scope that
     narrows them to that collection.

     This replaces treating the handle AS the shard filename, which only ever
     worked where the two happened to be the same word. /collections/pokemon
     matched; /collections/magic-the-gathering asked for a shard of that name
     and got a 404, and every set collection missed the same way, which is why
     those pages silently stayed on XCloud.

     Returning null leaves the page exactly as it is. That is the right answer
     for a collection we cannot scope: showing a shopper the whole game when
     they asked for one set would be worse than not filtering at all. */
  function plan(base, handle) {
    return fetchJson(mapUrl(base)).then(function (map) {
      var entry = map && map.collections && map.collections[handle];
      /* A miss is far more often a stale page than a genuinely unscopable
         collection, so the map is asked for once more past every cache before
         the page gives up. Only on a miss, and only once. */
      if (!entry) {
        return fetchMapPastCache().then(function (fresh) {
          var freshEntry = fresh && fresh.collections && fresh.collections[handle];
          if (freshEntry) {
            if (window.console) {
              console.info('[llf] ' + handle + ' was missing from the cached map, using a fresh one');
            }
            return usePlan(fresh, freshEntry);
          }
          if (window.console) console.warn('[llf] ' + handle + ' is not in the collection map, leaving page as is');
          return null;
        }, function () {
          /* The retry itself failed, so we are no worse off than before it
             existed: the page keeps whatever it does without us. */
          if (window.console) console.warn('[llf] ' + handle + ' is not in the collection map, leaving page as is');
          return null;
        });
      }
      return usePlan(map, entry);
    });

    function usePlan(map, entry) {
      var shards = (entry.shards && entry.shards[0] === '*')
        ? (map.shards || []) : (entry.shards || []);
      if (!shards.length) return null;

      var opts = {
        indexUrls: shards.map(function (g) { return shardUrl(base, g); }),
        scope: {
          facet: entry.facet, value: entry.value,
          maxPrice: entry.maxPrice, withinDays: entry.withinDays
        }
      };
      if (!entry.members) return opts;

      /* Vendor shelves, Japanese, Binders: no facet describes them, so the
         membership list is fetched, and only on the page that needs it. */
      return fetchJson(membersUrl(base, handle, entry)).then(function (mem) {
        opts.scope.members = (mem && mem.handles) || [];
        return opts;
      });
    }
  }

  /* ---- search ---------------------------------------------------------------
     Shopify does the matching and the ranking; we do everything after it.

     The engine only ever needed a candidate list, and on a collection that is
     the membership file. Search has no membership file, and the index carries
     no titles, so it cannot do the matching itself. /search?view=handles
     returns Shopify's own result set as handles in Shopify's own relevance
     order, which is both the candidate list and the default ordering.

     Every shard is loaded because a search crosses games by definition. That
     is the same thing /collections/all already does.

     Shopify stops serving results at roughly 1,000, so the fetch stops at four
     pages: a fifth would come back empty and cost a round trip to learn it. */
  /* ---- brands ---------------------------------------------------------------
     /collections/<handle>?view=vendors returns handle and vendor pairs, 250 a
     page. Asked for only where a Brand filter can say something: the section
     reports how many vendors the collection has (all_vendors, which covers the
     whole collection, not just the rendered page) and how many products. One
     vendor means the group would be a single tickbox, and past 1,000 products
     the pages are not worth fetching for a facet that big collections, all one
     publisher, would not use. Cached for the session, keyed on the product
     count so an upload or removal refetches. A failure returns null and the
     page filters exactly as before, just without Brand. */
  function brandKey(name) {
    var s = String(name).toLowerCase();
    if (s.normalize) s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return s.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function loadVendors(tag, handle) {
    var kinds = parseInt(tag.getAttribute('data-ll-vendor-count'), 10) || 0;
    var count = parseInt(tag.getAttribute('data-ll-product-count'), 10) || 0;
    if (!handle || kinds < 2 || count < 2 || count > 1000) return Promise.resolve(null);

    var key = 'llf:vendors:' + handle;
    try {
      var hit = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (hit && hit.count === count && hit.t > Date.now() - 30 * 60000) return Promise.resolve(hit.data);
    } catch (e) {}

    /* NOT fetchJson. That sends Accept: application/json, and on a collection
       URL Shopify answers that header with its own collection JSON and ignores
       ?view entirely, so the vendor feed never ran. A plain request gets the
       template, whose body is JSON, parsed here. */
    function page(n) {
      return fetch('/collections/' + encodeURIComponent(handle) + '?view=vendors&page=' + n)
        .then(function (r) {
          if (!r.ok) throw new Error('vendors ' + r.status);
          return r.text();
        })
        .then(function (t) { return JSON.parse(t); })
        .catch(function () { return null; });
    }

    return page(1).then(function (first) {
      if (!first || !first.vendors) return null;
      var pages = Math.min(first.pages || 1, 4);
      var rest = [];
      for (var n = 2; n <= pages; n++) rest.push(page(n));
      return Promise.all(rest).then(function (more) {
        var map = {};
        var names = {};
        [first].concat(more).forEach(function (p) {
          if (!p || !p.vendors) return;
          Object.keys(p.vendors).forEach(function (h) {
            var name = String(p.vendors[h] || '').trim();
            if (!name) return;
            var k = brandKey(name);
            if (!k) return;
            map[h] = k;
            if (!names[k]) names[k] = name;
          });
        });
        var data = { map: map, names: names };
        try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), count: count, data: data })); } catch (e) {}
        return data;
      });
    }).catch(function () { return null; });
  }

  function planSearch(base, query) {
    if (!query) return Promise.resolve(null);

    function page(n) {
      return fetchJson('/search?q=' + encodeURIComponent(query) +
        '&view=handles&type=product&page=' + n).catch(function () { return null; });
    }

    return page(1).then(function (first) {
      if (!first || !first.handles || !first.handles.length) return null;
      var pages = Math.min(first.pages || 1, 4);
      var rest = [];
      for (var n = 2; n <= pages; n++) rest.push(page(n));
      return Promise.all(rest).then(function (more) {
        var handles = first.handles.slice();
        more.forEach(function (p) { if (p && p.handles) handles = handles.concat(p.handles); });
        return {
          indexUrls: (assetUrls.map ? Object.keys(assetUrls) : [])
            .filter(function (k) { return k !== 'map'; })
            .map(function (g) { return shardUrl(base, g); }),
          /* Relevance order is preserved as the members order, so the engine's
             own "collection order" column becomes the relevance column and the
             page opens on it. */
          scope: { members: handles },
          searchTotal: first.total || handles.length
        };
      });
    }).catch(function () { return null; });
  }

  /* Every way this engine can decline to run.

     Both exits used to be silent, and silence was the right answer while
     XCloud was installed: its filters were still sitting there, so leaving the
     page as it was left the shopper with something that worked. Once XCloud is
     gone, the same silence leaves a filter heading over an empty panel and no
     way to narrow anything.

     So a stand-down now says so, and each page decides what to show for it.
     "unscoped" is a collection this engine has no index for, which is a
     configuration gap; "unavailable" is the index failing to load, which is
     usually transient and worth a retry. */
  function standDown(reason) {
    if (sheet) sheet.setAttribute('data-llf-fallback', reason);
    if (root) root.setAttribute('data-llf-fallback', reason);
    var llsLayout = document.querySelector('[data-lls-layout]');
    if (llsLayout) llsLayout.setAttribute('data-filters-ready', 'none');
    if (window.console) {
      console.warn('[llf] standing down (' + reason + '): filters will not run on this page');
    }
  }

  function boot() {
    var tag = document.querySelector('[data-ll-filter-base]');
    if (!tag || typeof window.LLFilter === 'undefined') return;

    /* The selectors come from the boot tag so the same engine can drive a page
       whose markup uses different class names. The collection section's names
       are the defaults, so nothing there had to change; the search section
       passes its own. */
    root = document.querySelector(tag.getAttribute('data-ll-root') || '.llc');
    grid = document.querySelector(tag.getAttribute('data-ll-grid') || '.llc__grid');
    sheet = document.querySelector('[data-filters]');
    if (!root || !grid || !sheet) return;

    var base = tag.getAttribute('data-ll-filter-base');
    var pageSize = parseInt(tag.getAttribute('data-ll-page-size'), 10);
    var mode = tag.getAttribute('data-ll-mode') || 'collection';

    try {
      assetUrls = JSON.parse(tag.getAttribute('data-ll-assets') || '{}');
    } catch (e) {
      assetUrls = {};
    }

    var planning;
    if (mode === 'search') {
      planning = planSearch(base, tag.getAttribute('data-ll-query') || '');
    } else {
      var handle = collectionHandle();
      if (!handle) return;
      planning = plan(base, handle);
    }

    planning.then(function (opts) {
      if (!opts) return null;
      opts.pageSize = pageSize;
      /* The section's own brand table, so a cloned card can be told which game
         it belongs to instead of inheriting the template card's. */
      opts.brands = tag.getAttribute('data-ll-brands') || '';
      opts.gameLabel = tag.getAttribute('data-ll-game-label') || '';
      opts.mode = mode;
      opts.hideReleased = tag.getAttribute('data-ll-hide-released') === '1';
      /* Only passed by a page whose card markup differs from the collection's. */
      if (tag.getAttribute('data-ll-cta-sel')) {
        opts.cta = {
          sel: tag.getAttribute('data-ll-cta-sel'),
          atc: tag.getAttribute('data-ll-cta-atc') || 'data-atc',
          atcId: tag.getAttribute('data-ll-cta-atc-id') || null,
          classes: tag.getAttribute('data-ll-cta-classes') !== '0',
          mini: tag.getAttribute('data-ll-cta-mini') || null
        };
      }
      var vendorsFor = mode === 'search' ? Promise.resolve(null) : loadVendors(tag, collectionHandle());
      return vendorsFor.then(function (vendors) {
        if (vendors) {
          opts.vendors = vendors.map;
          Object.keys(vendors.names).forEach(function (k) { BRAND_NAMES[k] = vendors.names[k]; });
        }
        return LLFilter.init(opts);
      });
    }).then(function (ready) {
      /* Null means the collection is not one we can scope, so the page keeps
         whatever it already does and nothing below runs. */
      if (!ready) { standDown('unscoped'); return; }

      /* Only now, once the index is known good, does XCloud get stood down.
         Failing to load must leave the page exactly as it was. */
      root.setAttribute('data-llf-on', '1');

      /* The panel goes under whatever heading the host sheet already has, and
         at the top when it has none. This used to assume .llc__filters-head
         and dereference it blind, so on the search page, whose sheet heading is
         .lls__filters-head, the whole boot threw here: the engine had loaded,
         scoped and stood XCloud down, and then no facets appeared at all. */
      var sheetHead = sheet.querySelector('.llc__filters-head, .lls__filters-head');
      sheet.insertBefore(buildPanel(), sheetHead ? sheetHead.nextSibling : sheet.firstChild);

      /* The search layout keeps its sidebar collapsed until something says it
         has filters worth showing. That flag was XCloud's; now it is ours. */
      var llsLayout = document.querySelector('[data-lls-layout]');
      if (llsLayout) llsLayout.setAttribute('data-filters-ready', '1');

      moreBtn = document.createElement('button');
      moreBtn.type = 'button';
      moreBtn.className = 'llf__more';
      moreBtn.addEventListener('click', loadMore);
      grid.parentNode.insertBefore(moreBtn, grid.nextSibling);

      wire();
      adoptNativeSort();
      state.selection = readUrl();

      /* A collection carries its own default sort, and until now this ignored
         it: every page opened on `featured`, so a collection set to Newest or
         Best selling was silently re-ordered the moment filtering took over.
         The section passes Shopify's own default_sort_by value, which maps
         onto these keys through each sort's `native`. */
      var defaultSort = tag.getAttribute('data-ll-default-sort');
      if (defaultSort) {
        var fromCollection = LLFilter.sorts.filter(function (s) {
          return s.native === defaultSort;
        })[0];
        if (fromCollection) {
          state.sort = fromCollection.key;
          if (nativeSort) nativeSort.value = fromCollection.native;
        }
      }

      /* An explicit choice in the URL still wins over the collection default. */
      var urlSort = new URLSearchParams(location.search).get('sort');
      if (urlSort) {
        state.sort = urlSort;
        if (nativeSort) {
          var pick = LLFilter.sorts.filter(function (s) { return s.key === urlSort; })[0];
          if (pick) nativeSort.value = pick.native;
        }
      }
      return refresh();
    }).catch(function (err) {
      /* Deliberately quiet for the shopper and loud for us: no index means the
         page keeps whatever it had, it does not break. */
      if (window.console) console.warn('[llf] index unavailable:', err && err.message);
      standDown('unavailable');
    });
  }

  /* Retry is a reload, deliberately. Re-running boot() in place would have to
     unpick whatever the failed attempt left behind, and the failure this is
     for is a request that did not arrive: a fresh page is both simpler and
     more likely to work. Delegated, so it covers either page's button. */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-llf-retry]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    location.reload();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}());
